import { Link } from "react-router-dom";
import "../styles/ILab.css";

const pillars = [
  { icon: "bi-cpu", title: "Better AI reasoning", body: "We study how to help AI solve problems more carefully, not simply sound confident." },
  { icon: "bi-diagram-3", title: "Consistent answers", body: "We work to make the same question produce the same clear, checkable answer." },
  { icon: "bi-patch-check", title: "Answers people can check", body: "We link answers to original sources, so people can check the facts themselves." },
  { icon: "bi-shield-lock", title: "Safe and controlled AI", body: "We build systems that remain secure, understandable, and under human control." },
];

const principles = [
  { n: "01", title: "Open by default", body: "We publish methods, results, and the findings that did not work too, not just the wins." },
  { n: "02", title: "Proof over claims", body: "Every claim includes the data, code, or proof that supports it." },
  { n: "03", title: "Others must be able to repeat it", body: "If a result cannot be reproduced, we do not consider the work done." },
  { n: "04", title: "Work on unsolved problems", body: "We take on the hard, unsolved problems that move the whole field forward." },
];

const threads = [
  { status: "Active", title: "Turning messy documents into reliable data", body: "Turning difficult public documents into organized data without inventing missing facts." },
  { status: "Active", title: "Answers linked to evidence", body: "Making sure every conclusion links back to the exact source that supports it." },
  { status: "Active", title: "Finding useful patterns in public records", body: "Finding useful patterns in public company and policy records while keeping the original sources attached." },
];

function ILab() {
  return (
    <main className="ix-page">
      <header className="ix-hero center">
        <div className="ix-shell">
          <span className="ix-eyebrow">Intellusion iLab</span>
          <h1>Making AI easier to trust.</h1>
          <p className="ix-hero-lead">
            iLab is where we test new ways to make AI more reliable, easier to check,
            and safer to use. We publish what we learn.
          </p>
          <div className="home-hero-btns" style={{ justifyContent: "center", marginTop: "30px" }}>
            <a href="#research" className="ix-btn ix-btn--primary">Explore our research →</a>
            <Link to="/contact" className="ix-btn ix-btn--ghost">Collaborate with iLab</Link>
          </div>
        </div>
      </header>

      {/* THESIS */}
      <section className="ix-section">
        <div className="ix-shell">
          <div className="ix-sec-head">
            <span className="ix-eyebrow is-gray">Why iLab exists</span>
            <h2>Good AI should show how it reached an answer.</h2>
            <p>
              Every Intellusion product depends on careful research. We study how to
              make answers consistent, link them to evidence, and keep systems safe as
              they become more capable. We share our findings so others can build on
              them.
            </p>
          </div>
        </div>
      </section>

      {/* O-FUNNEL RELEASE */}
      <section className="ix-section" id="releases">
        <div className="ix-shell">
          <div className="ix-sec-head">
            <span className="ix-eyebrow is-gray">Latest release</span>
            <h2>Open sourced from iLab.</h2>
          </div>
          <div className="lab-release">
            <div className="lab-release-head">
              <span className="front-tag is-building">New · Open source</span>
              <span className="lab-release-meta">Python library and paper</span>
            </div>
            <h3 className="lab-release-name">O-Funnel</h3>
            <p className="lab-release-tag">Pull the fields you need out of messy, changing documents.</p>
            <p className="lab-release-body">
              Declare the fields you need once. O-Funnel reads XML, JSON, CSV, HTML or
              key value text into one structure, verifies that nothing was lost, and
              finds each field from several clues at once, so a renamed key or a new
              format does not break it. When a field is genuinely missing it tells you
              why instead of guessing, and it learns new key names from the data it
              could not place, getting better the longer it runs.
            </p>
            <div className="lab-release-stat">
              On 34,989 real PubMed records it matched a hand written parser exactly.
              After five elements were renamed, the parser regexes fell to{" "}
              <strong>0.20 F1</strong> while O-Funnel held at <strong>1.00</strong>.
              Pure Python, zero dependencies.
            </div>
            <div className="lab-install">
              <span className="lab-install-p">$</span>
              <code>pip install ofunnel</code>
            </div>
            <div className="home-hero-btns">
              <a className="ix-btn ix-btn--primary" href="https://github.com/osamaa-mustafa/ofunnel" target="_blank" rel="noopener noreferrer">Code on GitHub →</a>
              <a className="ix-btn ix-btn--ghost" href="https://arxiv.org/abs/2609.39209" target="_blank" rel="noopener noreferrer">Read the paper</a>
            </div>
          </div>
        </div>
      </section>

      {/* RESEARCH FOCUS */}
      <section className="ix-section">
        <div className="ix-shell">
          <div className="ix-sec-head">
            <span className="ix-eyebrow is-gray">What we work on</span>
            <h2>Research focus.</h2>
          </div>
          <div className="ix-grid-2">
            {pillars.map((p) => (
              <div className="ix-card lab-pillar" key={p.title}>
                <i className={`bi ${p.icon} lab-pillar-i`} />
                <div>
                  <h4 style={{ fontSize: "20px", margin: "0 0 8px" }}>{p.title}</h4>
                  <p style={{ fontSize: "15px", margin: 0 }}>{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="ix-section">
        <div className="ix-shell">
          <div className="ix-sec-head">
            <span className="ix-eyebrow is-gray">How we work</span>
            <h2>Principles.</h2>
          </div>
          <div className="ix-grid-2">
            {principles.map((p) => (
              <div className="ix-card" key={p.n}>
                <span className="ix-card-i">{p.n}</span>
                <h4 style={{ fontSize: "20px", margin: "12px 0 8px" }}>{p.title}</h4>
                <p style={{ fontSize: "15px", margin: 0 }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESEARCH & PUBLICATIONS */}
      <section className="ix-section" id="research">
        <div className="ix-shell">
          <div className="ix-sec-head">
            <span className="ix-eyebrow is-gray">Research and publications</span>
            <h2>What we are working on.</h2>
          </div>
          <div className="ix-grid-3">
            {threads.map((t) => (
              <div className="ix-card" key={t.title}>
                <span className="front-tag is-research">{t.status}</span>
                <h4 style={{ fontSize: "19px", margin: "14px 0 8px" }}>{t.title}</h4>
                <p style={{ fontSize: "15px", margin: 0 }}>{t.body}</p>
              </div>
            ))}
          </div>
          <div className="home-hero-btns" style={{ marginTop: "32px" }}>
            <a className="ix-btn ix-btn--ghost" href="https://arxiv.org/abs/2609.39209" target="_blank" rel="noopener noreferrer">O-Funnel paper</a>
            <a className="ix-btn ix-btn--ghost" href="https://github.com/Intellusion" target="_blank" rel="noopener noreferrer">GitHub</a>
            <a className="ix-btn ix-btn--ghost" href="https://www.linkedin.com/company/intellusion-ilab/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="home-cta">
        <div className="ix-shell">
          <span className="ix-eyebrow">Work with iLab</span>
          <h2>Have a difficult AI problem?</h2>
          <div className="home-hero-btns" style={{ justifyContent: "center" }}>
            <Link to="/contact" className="ix-btn ix-btn--primary">Get in touch →</Link>
            <Link to="/" className="ix-btn ix-btn--ghost">Explore Intellusion</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ILab;
